import { useEffect, useState, useMemo } from 'react';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { isEmpty } from 'lodash';
import {
  useAppSelector,
  usePublishToBotMutation,
  getCustomerInvite,
  getSelectedItems,
  getWorkspaceId,
  PaymentAccountsMethodType,
} from '@zonofi/zono-money-store';
import { usePaymentMethods } from './use-payment-methods';
import { onClickBackToWhatsapp } from '../../helper';

export const useSendPaymentRequest = () => {
  const selectedInvoices = useAppSelector(getSelectedItems);
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const inviteDetails = useAppSelector(getCustomerInvite);
  const { seller, waConfig } = inviteDetails;

  const { paymentMethodData } = usePaymentMethods();
  const [publishToBot, publishToBotRes] = usePublishToBotMutation();
  const [successState, setSuccessState] = useState(false);

  const getUpiDetails = (id: string) => {
    const item = paymentMethodData.find(
      (method): method is Extract<PaymentAccountsMethodType, { type: 'upi' }> =>
        method?.id === id && method?.type === 'upi'
    );
    return {
      upiId: item?.upiId,
      upiName: item?.upiAccountHolderName,
      qrCodeUrl: item?.qrCodeUrl,
    };
  };

  const getBankDetails = (id: string) => {
    const item = paymentMethodData.find(
      (
        method
      ): method is Extract<PaymentAccountsMethodType, { type: 'bank' }> =>
        method?.id === id && method?.type === 'bank'
    );
    return {
      accountNumber: item?.accountNumber,
      accountHolderName: item?.accountHolderName,
      ifscCode: item?.ifscCode,
      bankName: item?.bankName,
    };
  };

  const paymentRequestPayload = (
    selectedMethods: Record<string, string | null>
  ) => {
    const sentPaymentRequestPayload =
      selectedInvoices?.map((item) => ({
        amount: item?.invoiceAmount - item?.paidAmount,
        inviteId: item?.inviteId,
        invoiceId: item?.id,
        docNumber: item?.docNumber,
        invoiceDate: item?.invoiceDate,
      })) ?? [];

    const sortedPaymentRequestPayload = sentPaymentRequestPayload
      .sort((a, b) => moment(b?.invoiceDate).diff(moment(a?.invoiceDate)))
      .map((item) => ({
        ...item,
        invoiceDate: moment(item?.invoiceDate).format('Do MMM YYYY'),
      }));

    return {
      type: 'PaymentRequest',
      intent: 'PaymentRequest',
      conversation: {
        id: uuidv4(),
      },
      workspaceId: sellerWorkspaceId,
      text: 'Payment Request',
      sendPaymentRequest: {
        invoiceList: sortedPaymentRequestPayload,
        ...(!isEmpty(selectedMethods?.upi)
          ? { upiAccount: getUpiDetails(String(selectedMethods?.upi)) }
          : {}),
        ...(!isEmpty(selectedMethods?.bank)
          ? { bankAccount: getBankDetails(String(selectedMethods?.bank)) }
          : {}),
      },
    };
  };

  const onSendPaymentRequest = (
    selectedMethods: Record<string, string | null>
  ) => {
    const formData = new FormData();
    const payload = paymentRequestPayload(selectedMethods);
    formData.append('activity', JSON.stringify(payload));

    publishToBot({
      sellerWorkspaceId,
      body: { activity: payload },
    });
  };

  useEffect(() => {
    if (publishToBotRes.isSuccess) {
      setSuccessState(true);
    }
  }, [publishToBotRes]);

  const onWhatsAppPress = () => {
    onClickBackToWhatsapp({
      whatsappPhoneNo: waConfig?.whatsappPhoneNo,
      workspaceId: seller?.workspaceId,
    });
  };

  const paymentRequestDescription = useMemo(() => {
    return `Payment request has been sent for ${selectedInvoices?.length} Invoices.`;
  }, [selectedInvoices]);

  return {
    onSendPaymentRequest,
    isLoading: publishToBotRes.isLoading,
    isSuccess: publishToBotRes.isSuccess,
    successState,
    setSuccessState,
    paymentRequestDescription,
    onWhatsAppPress,
  };
};
